import type { HiringLink } from '../../services/api';

/**
 * A link as the server answers it to ONE action - an answer to a question, a check of a
 * raise, a settle, a hiring request confirmed - is not a link of a list. Two things differ,
 * and the Hiring tab puts such an answer straight into its rows, so both are settled here.
 *
 * 1. THE FIGURES WERE NOT READ. The server reads nothing of Recruitment for such an answer,
 *    so the link comes back as "Numbers not available" (`not_read`). The row on show was
 *    read a moment ago: it keeps its own status and figures, and takes the rest from the
 *    answer (keepRowReading).
 * 2. `needsAttention` MAY BE MISSING. Only a list, and an answer to a question, say it.
 *    Whether such a link needs a person is then worked out from its own open questions,
 *    by the server's own rule (linkNeedsAPerson).
 *
 * And one thing is the same, whatever the answer left out: A CANCEL THAT IS OPEN STAYS ON
 * THE ROW. An answer that still marks its link `cancel_open` and carries no cancel line
 * did not read where that cancel stands (an answer to a question does; an older server
 * does not). The row's own line then stands: without it the row would stop saying
 * "Recruitment did not confirm the cancel", and the card would offer to cancel again.
 */

/** The one open question a later read answers by itself: it never needs a person. */
const ANSWERS_ITSELF = 'team_unconfirmed';
/** The open question a cancel leaves while Recruitment has not been seen to delete the opening. */
const CANCEL_OPEN = 'cancel_open';

/**
 * Does this link need a person? The server's own word when it sent one; otherwise its own
 * rule: not closed, and an open question other than the one a later read answers.
 */
export const linkNeedsAPerson = (link: Pick<HiringLink, 'state' | 'needsAttention' | 'attention'>): boolean => {
  if (link.state === 'closed') return false;
  if (typeof link.needsAttention === 'boolean') return link.needsAttention;
  return (link.attention ?? []).some((reason) => reason !== ANSWERS_ITSELF);
};

/** A question that was open on the row and is no longer open on the answer: it was just answered. */
const answered = (row: HiringLink, answer: HiringLink, reason: string): boolean =>
  (row.attention ?? []).includes(reason) && !(answer.attention ?? []).includes(reason);

/**
 * The link to show for an answer that did not read the figures, where the row on show has
 * them: the row's own status, figures, candidate counts and "now" address, with everything
 * else - the questions left, the address or the positions accepted - from the answer.
 *
 * Only for a live link that was live, on the same opening. A request that has just become
 * an opening has no reading to keep, and a closed link shows what the server said.
 * A flag whose question was just answered goes with it: "Still this store" made the new
 * address the accepted one, so "address changed" is no longer true of the row's reading.
 */
export const keepRowReading = (row: HiringLink | null | undefined, answer: HiringLink): HiringLink => {
  if (!row || row.id !== answer.id) return answer;
  if (answer.state !== 'live' || answer.unavailableReason !== 'not_read') return answer;
  if (row.state !== 'live' || row.openingId !== answer.openingId) return answer;
  return {
    ...answer,
    status: row.status,
    statusLabel: row.statusLabel,
    numbers: row.numbers,
    numbersFrom: row.numbersFrom,
    stages: row.stages,
    stagesFrom: row.stagesFrom,
    seenAt: row.seenAt,
    lastKnownStatus: row.lastKnownStatus,
    unavailableReason: row.unavailableReason,
    // (A link from an older server may carry no flags: then the answer's own stand.)
    flags: row.flags ? {
      ...row.flags,
      addressChanged: row.flags.addressChanged && !answered(row, answer, 'address_changed'),
      positionsChanged: row.flags.positionsChanged && !answered(row, answer, 'positions_changed'),
    } : answer.flags,
    address: { linked: answer.address?.linked ?? null, now: row.address?.now ?? null },
    // Still marked as having a cancel open, and the answer does not say where it stands: the row does.
    cancel: answer.cancel ?? ((answer.attention ?? []).includes(CANCEL_OPEN) ? row.cancel ?? null : null),
  };
};
